import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState, selector, atom } from "recoil";
import {
  Currency,
  currentUserCurrencyAtom,
  currentLocaleAtom,
} from "./AuthProvider";
import { getDivisor } from "./services/utils";
import { useIntl, IntlShape } from "react-intl";
import { GenericObject } from "./services/Price";
import { Typography } from "@mui/material";
import { useQuery } from "react-query";
import { listProperties } from "./Calendar/Filter/PropertySelector";

const MILLION_SCALE = 1000000;
const THOUSAND_SCALE = 1000;

export class FormatterService {
  currencyCode: string;
  locale: string;
  formatters: { [key: string]: Intl.NumberFormat };

  constructor(currencyCode: string, locale: string) {
    this.currencyCode = currencyCode;
    this.locale = locale;
    this.formatters = {};
  }
  getFormatter(options: Intl.NumberFormatOptions) {
    const hash = hashObject(options);
    return this.formatters[hash];
  }
  createFormatter(options: Intl.NumberFormatOptions) {
    const _options: Intl.NumberFormatOptions =
      options.style === "currency"
        ? {
            ...options,
            currency: this.currencyCode,
            currencyDisplay: "narrowSymbol",
          }
        : options;
    const hash = hashObject(_options);
    const newFormatter = new Intl.NumberFormat(this.locale, _options);
    this.formatters[hash] = newFormatter;

    return newFormatter;
  }
  format(value: number, options: Intl.NumberFormatOptions) {
    let f = this.getFormatter(options);

    if (!f) {
      f = this.createFormatter(options);
    }

    return f.format(value);
  }
  formatToParts(value: number, options: Intl.NumberFormatOptions) {
    let f = this.getFormatter(options);

    if (!f) {
      f = this.createFormatter(options);
    }

    return f.formatToParts(value);
  }
}

/**
 * Store formatter Service
 *
 * @remarks
 * Use dangerouslyAllowMutability to not freeze object
 */
export const formatterService = selector({
  key: "formattersListSelector",
  get: ({ get }) => {
    const currency = get(currentUserCurrencyAtom);
    const local = get(currentLocaleAtom);

    return new FormatterService(currency.code, local);
  },
  dangerouslyAllowMutability: true,
});

/**
 * Currency of the property shown by the current page (Calendar, Cockpit).
 * Null when no property-specific currency is set (falls back to tenant default).
 */
export const propertyCurrencyAtom = atom<Currency | null>({
  key: "propertyCurrency",
  default: null,
});

/**
 * Publishes `currency` into `propertyCurrencyAtom` while the calling page is
 * mounted, and clears it on unmount so another page can't inherit it.
 */
export const usePropertyCurrency = (
  currency: Currency | null | undefined
): void => {
  const setPropertyCurrency = useSetRecoilState(propertyCurrencyAtom);

  useEffect(() => {
    setPropertyCurrency(currency ?? null);
    return () => setPropertyCurrency(null);
  }, [currency, setPropertyCurrency]);
};

/**
 * Formatter service using the currency of the property shown by the current
 * page (Calendar, Cockpit) when available, falling back to the tenant default.
 */
export const propertyFormatterService = selector({
  key: "propertyFormatterService",
  get: ({ get }) => {
    const propertyCurrency = get(propertyCurrencyAtom);
    const tenantCurrency = get(currentUserCurrencyAtom);
    const local = get(currentLocaleAtom);
    const currency = propertyCurrency ?? tenantCurrency;

    return new FormatterService(currency.code, local);
  },
  dangerouslyAllowMutability: true,
});

/**
 * Maps property IDs to their currency.
 * Populated globally (e.g. by the Alert table) so any component can resolve
 * a per-property currency without re-fetching.
 */
export const propertyCurrencyMapAtom = atom<Record<number, Currency>>({
  key: "propertyCurrencyMap",
  default: {},
});

/**
 * Creates one FormatterService per unique currency code found in
 * propertyCurrencyMapAtom, keyed by currency code.
 */
export const currencyCodeFormatterMapSelector = selector<
  Record<string, FormatterService>
>({
  key: "currencyCodeFormatterMap",
  get: ({ get }) => {
    const currencyMap = get(propertyCurrencyMapAtom);
    const locale = get(currentLocaleAtom);
    const result: Record<string, FormatterService> = {};
    for (const currency of Object.values(currencyMap)) {
      if (!result[currency.code]) {
        result[currency.code] = new FormatterService(currency.code, locale);
      }
    }
    return result;
  },
  dangerouslyAllowMutability: true,
});

const fetchAllPropertiesCurrencies = async (): Promise<
  Record<number, Currency>
> => {
  const map: Record<number, Currency> = {};
  let page = 1;
  let hasNext = true;

  // we set a limit of 100 pages to make sure we don't get into infinite loop even though hasNext should be enough
  while (hasNext && page <= 100) {
    const data = await listProperties({ page_size: 100, page });
    if (data?.results) {
      for (const property of data.results) {
        if (property.currency) {
          map[property.id] = property.currency;
        }
      }
    }
    hasNext = !!data?.next;
    page++;
  }

  return map;
};

/**
 * Fetches all properties (paginated, max 100/page) and populates
 * propertyCurrencyMapAtom. Call once at the top of any page that needs
 * per-property currency formatting.
 */
export const usePropertyCurrencyMap = () => {
  const existingMap = useRecoilValue(propertyCurrencyMapAtom);
  const setPropertyCurrencyMap = useSetRecoilState(propertyCurrencyMapAtom);

  useQuery(["properties-currency-map"], fetchAllPropertiesCurrencies, {
    enabled: Object.keys(existingMap).length === 0,
    onSuccess: (map) => setPropertyCurrencyMap(map),
  });
};

const hashObject = (options: Intl.NumberFormatOptions) => {
  return JSON.stringify(sortObj(options));
};

export type UnitType =
  | "currency"
  | "percent"
  | "pts"
  | "night"
  | "week"
  | "sales";

/**
 * Format value with formatToParts
 *
 * @remarks
 * Usefull for currency with specific unit (k, M)
 *
 * @param value
 * @param formatter
 * @param formatOptions
 * @param unitValue
 */
export function formatValue(
  value: number,
  formatter: FormatterService,
  formatOptions: Intl.NumberFormatOptions,
  unitValue?: string
) {
  return formatter
    .formatToParts(value, formatOptions)
    .map((item) => {
      return item.type === "currency" && unitValue
        ? unitValue + item.value
        : item.value;
    })
    .join("");
}

/**
 * Check scale of number
 *
 * @remarks
 * Check scale of number and add unit (k, M)
 *
 * @param value
 */
export function formatScale(value: number) {
  if (value >= MILLION_SCALE || value <= -MILLION_SCALE) {
    return { value: value / MILLION_SCALE, unit: "M" };
  } else if (value >= THOUSAND_SCALE || value <= -THOUSAND_SCALE) {
    return { value: value / THOUSAND_SCALE, unit: "k" };
  } else {
    return { value: value, unit: "" };
  }
}

const formatKpi = (
  value: string,
  formatter: FormatterService,
  unit: UnitType,
  intl: IntlShape,
  currencyCode: string,
  intlOptions?: Intl.NumberFormatOptions,
  options?: GenericObject
) => {
  const isCurrencyValue = unit === "currency";
  const isSales = unit === "sales";
  let formatOptions: Intl.NumberFormatOptions = {
    style: isCurrencyValue ? "currency" : undefined,
    currency: isCurrencyValue ? currencyCode : undefined,
    currencyDisplay: isCurrencyValue ? "narrowSymbol" : undefined,
    maximumFractionDigits: isSales ? 2 : unit === "percent" ? 0 : 1,
    ...intlOptions,
  };
  const _value = Number(value);

  if (isCurrencyValue) {
    if (options && options.kUnit === false) {
      return formatter.format(_value, formatOptions);
    } else {
      let formattedScale = formatScale(_value);
      return formatValue(
        formattedScale.value,
        formatter,
        formatOptions,
        formattedScale.unit
      );
    }
  } else {
    if (unit === "percent") {
      return `${formatter.format(_value, formatOptions)} %`;
    } else if (unit === "pts") {
      let formattedScale = formatScale(_value);
      return `${formatter.format(formattedScale.value, formatOptions)} ${
        formattedScale.unit
      } pts`;
    } else if (unit === "night") {
      let formattedScale = formatScale(_value);
      return `${formatter.format(formattedScale.value, formatOptions)} ${
        formattedScale.unit
      } ${intl.formatMessage({
        id: "nuits",
      })}`;
    } else if (unit === "week") {
      let formattedScale = formatScale(_value);
      return `${formatter.format(formattedScale.value, formatOptions)} ${
        formattedScale.unit
      } ${intl.formatMessage({
        id: "WEEKS",
      })}`;
    } else if (unit === "sales") {
      let formattedScale = formatScale(_value);
      return `${formatter.format(formattedScale.value, formatOptions)} ${
        formattedScale.unit
      }`;
    } else if (unit === "pax") {
      let formattedScale = formatScale(_value);
      return `${formatter.format(formattedScale.value, formatOptions)} ${
        formattedScale.unit
      } pax`;
    }
  }
};

/**
 * Format Kpi tile number
 *
 * @param value
 * @param unit
 * @param intlOptions
 */
export const useFormatKpiValue = ({
  value,
  unit,
  intlOptions,
  options,
}: {
  value: string;
  unit: UnitType;
  intlOptions?: Intl.NumberFormatOptions;
  options?: GenericObject;
}) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  const currency = useRecoilValue(currentUserCurrencyAtom);
  const [formattedValue, setFormattedValue] = useState(() =>
    formatKpi(value, formatter, unit, intl, currency.code, intlOptions, options)
  );

  useEffect(() => {
    setFormattedValue(
      formatKpi(
        value,
        formatter,
        unit,
        intl,
        currency.code,
        intlOptions,
        options
      )
    );
  }, [value, formatter, unit, intl, currency.code, intlOptions, options]);

  return formattedValue;
};

/**
 * Format Kpi tile number
 *
 * @remarks
 * This function component is called for formatted Kpi tile on dashboard page
 * (currency, percent, fraction, ...)
 *
 * @param value - value to format
 * @param cssClass - class css
 * @param styles
 * @param intlOptions
 */
export const KpiNumberFormatter = ({
  value,
  unit,
  styles,
  classes,
  intlOptions,
  options,
  displayPlusSign,
}: {
  value: string;
  unit: UnitType;
  styles?: GenericObject;
  classes?: string;
  intlOptions?: Intl.NumberFormatOptions;
  options?: GenericObject;
  displayPlusSign?: boolean;
}) => {
  const formattedValue = useFormatKpiValue({
    value,
    unit,
    intlOptions,
    options,
  });

  return (
    <Typography sx={styles} className={classes}>
      {displayPlusSign && Number(value) > 0
        ? `+${formattedValue}`
        : formattedValue}
    </Typography>
  );
};

/**
 * Format Kpi tile number
 *
 * @remarks
 * This hook is called for formatted Kpi tile on dashboard page
 * (currency, percent, fraction, ...)
 *
 * @param value - value to format
 * @param cssClass - class css
 * @param intlOptions
 */
export const useKpiNumberFormatter = (
  value: string,
  unit: UnitType,
  intlOptions?: Intl.NumberFormatOptions
) => {
  const formattedValue = useFormatKpiValue({ value, unit, intlOptions });

  if (!value) return null;

  return formattedValue;
};

// TODO: replace types any
const sortObj = (object: any) => {
  return Object.keys(object)
    .sort()
    .reduce((objEntries: any, key) => {
      objEntries[key] = object[key];
      return objEntries;
    }, {});
};

/**
 * Format value for Graph Pie
 *
 * @remarks
 * This hook is called for formatted Graph Pie on reporting page
 * ("XXX k currency")
 *
 * @param value - value to format
 * @param options - option form formating
 * @param direction - value from backend to know if it's negartive or positive value
 */
export const usePieGraphCurrencyValueFormatter = (
  value: number,
  options: Intl.NumberFormatOptions,
  direction?: string
) => {
  const formatter = useRecoilValue(formatterService);
  let formattedValue;
  let valueToFormat = value;
  let kValue = false;

  if (direction === "Perte") {
    if (value > 1000) {
      valueToFormat = -value / 1000;
      kValue = true;
    }
  } else if (value > 1000) {
    valueToFormat = value / 1000;
    kValue = true;
  }

  if (kValue && !options.style) {
    formattedValue = formatter.format(valueToFormat, options) + "k";
  } else {
    formattedValue = formatter
      .formatToParts(valueToFormat, options)
      .map((item) => {
        return item.type === "currency" && kValue
          ? "k" + item.value
          : item.value;
      })
      .join("");
  }

  return formattedValue;
};

/**
 * Format value round currency
 *
 * @param formatter
 * @param value
 * @param options
 */
export const formatRoundCurrency = (
  formatter: FormatterService,
  value: number,
  options: Intl.NumberFormatOptions
) => {
  const divisor = getDivisor(value);
  const newValue = divisor ? value / divisor.value : value;

  const parts = formatter
    .formatToParts(newValue, options)
    .map((item) =>
      item.type === "currency"
        ? divisor
          ? divisor.symbol + item.value
          : item.value
        : item.value
    );

  return parts.join("");
};

/**
 * Format currency with "k" unit
 *
 * @remarks
 * This function his use for format currency with K unit but without change decimal
 *
 * @param formatter
 * @param value
 * @param options
 */
export const formatKUnitIntact = (
  formatter: FormatterService,
  value: number,
  options: Intl.NumberFormatOptions
) => {
  let _options = { ...options };

  if (value >= 1 || value === 0) {
    _options = { ...options, maximumFractionDigits: 0 };
  } else {
    _options = { ...options, maximumFractionDigits: 3 };
  }

  const parts = formatter.formatToParts(value, _options).map((item) => {
    return item.type === "currency" ? "k" + item.value : item.value;
  });

  return parts.join("");
};

/**
 * Format currency with "k" unit
 *
 * @remarks
 * Format currency for number under 1k to have this format : 0,000
 * Exp: 123€ => 0,123 k€
 *
 * @param formatter
 * @param value
 * @param options
 */
export const formatKUnit = (
  formatter: FormatterService,
  value: number,
  options: Intl.NumberFormatOptions
) => {
  const divisor = getDivisor(value, true);
  const newValue = divisor ? value / divisor.value : value;
  let _options = { ...options };

  if (value > 0 && value < 1000) {
    _options = {
      ..._options,
      maximumFractionDigits: 3,
    };
  }

  const parts = formatter.formatToParts(newValue, _options).map((item) => {
    return item.type === "currency"
      ? divisor
        ? divisor.symbol + item.value
        : item.value
      : item.value;
  });

  return parts.join("");
};

export const formatSimpleUnitIntact = (
  formatter: FormatterService,
  value: number
) => {
  return formatter.format(value, {
    style: "currency",
    maximumFractionDigits: value >= 1 || value === 0 ? 0 : 3,
  });
};

export const formatPercent = (formatter: FormatterService, value: number) => {
  return formatter.format(value, {
    style: "percent",
  });
};

// KpisTiles Helpers
export const getKpiValue = (
  value: number,
  helper: (value: number) => number
): number => {
  /**
   *
   */
  if (helper) return helper(value);
  return value;
};

export const getValueInPercent = (value: number): number => {
  return value * 100;
};

export const getValueBy7 = (value: number): number => {
  return value / 7;
};

export const getValueTimes7 = (value: number): number => {
  return value * 7;
};

export const getVariation = (value_n: number, value_n1: number): string => {
  return (value_n - value_n1).toFixed(1);
};

export const getVariationInPercent = (
  value_n: number,
  value_n1: number
): string => {
  if (value_n1 !== 0) {
    let r = (value_n - value_n1) / value_n1;
    r *= 100;

    return r.toFixed(1);
  }

  return "0";
};
